import { type ActionFunction } from "@remix-run/node";
import { Form, useActionData } from "@remix-run/react";
import { validateName, validateEmail } from "~/utils";
import { formSubmit } from "~/api";
import { ErrorBoundaryLayoutComponent } from "~/shared";
import type { ErrorBoundaryComponent } from "@remix-run/node";
import toast, { Toaster } from "react-hot-toast";
import { useRemixSubmit } from "~/hooks/use_remix_submit";
import { useEffect, useRef } from "react";

export const action: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  if (formData.get("action") === "send") {
    const email = formData.get("email");
    const name = formData.get("name");
    const body = formData.get("body");
    const formErrors = {
      name: validateName(String(name)),
      email: validateEmail(String(email)),
      body: validateName(String(body)),
    };

    if (Object.values(formErrors).some(Boolean)) return { formErrors };

    try {
      const response = await formSubmit({ name, email, body });

      return {
        success: true,
        data: response.data,
        formErrors,
      };
    } catch (e) {
      return {
        success: false,
        data: [],
        formErrors,
      };
    }
  }
};

export default function () {
  const actionData = useActionData();
  // console.log("actionData===", {
  //   actionData,
  // });
  const onOpen = (text: string) => {
    return toast.success(text);
  };
  const { submit, transition } = useRemixSubmit({
    onSuccess: () =>
      onOpen("Vielen Dank für Deine Nachricht. Wir melden uns bei Dir."),
    // onError: (error) => {
    //   toast.error("Error submitting form ");
    // },
  });
  let isAdding = transition.state === "submitting";
  let formRef = useRef<HTMLFormElement>(null);
  let taskInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isAdding) {
      formRef.current?.reset();
      taskInputRef.current?.focus();
    }
  }, [isAdding]);

  return (
    <div className="mx-auto flex flex-col justify-center px-4 pb-10 md:px-1">
      <div className="flex items-center justify-center pb-8 md:max-w-6xl lg:mx-auto">
        <div className="w-screen py-6">
          <div className="max-w-4xl py-4">
            <h1 className="text-2xl font-bold text-black">Kontaktformular</h1>
            <p className="textsm py-2 text-tagsFontColor md:text-base">
              Du hast eine Frage zur Nutzung von ru-digital, eine Idee zur
              Weiterentwicklung? Dir ist ein Fehler aufgefallen? Wir freuen uns
              über eine Nachricht von Dir!
            </p>
          </div>
          <div className="mt-4 max-w-4xl">
            <Form
              method="post"
              onSubmit={() => submit({ type: "submit" })}
              ref={formRef}
            >
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  {/* <label
                    htmlFor="first-name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    First name
                  </label> */}
                  <input
                    placeholder="Name"
                    aria-invalid={Boolean(actionData?.formErrors?.name)}
                    aria-errormessage={
                      actionData?.formErrors?.name ? "name-error" : undefined
                    }
                    type="text"
                    name="name"
                    id="name"
                    autoComplete="given-name"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                  {actionData?.formErrors?.name ? (
                    <p
                      className="mr-2 text-red-500"
                      id="name-error"
                      role="alert"
                    >
                      {actionData.formErrors.name}
                    </p>
                  ) : null}
                </div>

                <div className="col-span-6 sm:col-span-3">
                  {/* <label
                    htmlFor="last-name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Last name
                  </label> */}
                  <input
                    aria-invalid={Boolean(actionData?.formErrors?.email)}
                    aria-errormessage={
                      actionData?.formErrors?.email ? "name-error" : undefined
                    }
                    type="email"
                    name="email"
                    id="email"
                    placeholder="E-Mail Adresse"
                    autoComplete="email"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                  {actionData?.formErrors?.email ? (
                    <p
                      className="mr-2 text-red-500"
                      id="email-error"
                      role="alert"
                    >
                      {actionData.formErrors.email}
                    </p>
                  ) : null}
                </div>

                <div className="col-span-6 mt-6">
                  <textarea
                    aria-invalid={Boolean(actionData?.fieldErrors?.body)}
                    aria-errormessage={
                      actionData?.fieldErrors?.body
                        ? "content-error"
                        : undefined
                    }
                    id="body"
                    name="body"
                    rows={3}
                    key="textarea"
                    className="block h-auto w-full rounded-md border border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm lg:h-80"
                    placeholder="Ihre Nachricht an uns…"
                    defaultValue={""}
                  />
                  {actionData?.formErrors?.body ? (
                    <p
                      className="mr-2 text-red-500"
                      id="content-error"
                      role="alert"
                    >
                      {actionData.formErrors.body}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="px-4 py-8 text-center sm:px-6">
                <button
                  type="submit"
                  name="action"
                  value="send"
                  className="inline-flex justify-center rounded-md border border-transparent bg-yellow-400 py-2 px-4 text-sm font-medium text-gray-700 shadow-sm  hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                  {transition.state === "loading"
                    ? "Sending...."
                    : "Anfrage senden"}
                </button>
              </div>
            </Form>
          </div>
        </div>
      </div>
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{ className: "text-sm", duration: 9000 }}
      />
    </div>
  );
}

export const ErrorBoundary: ErrorBoundaryComponent = ({ error }) => {
  console.error(error);
  return <ErrorBoundaryLayoutComponent error={error} />;
};
